import { type ReactElement, useEffect, useState } from 'react';
import { IconeCalendrier, IconeCarte, IconeCasesReliees, IconeHorloge, IconeListe } from '../partage/icones';
import { ANGLES, angleSuivant, type Vue } from './angles';
import './BasculeVue.css';

/*
 * Les angles, dans l'ordre des onglets, et l'angle suivant pour la touche : `angles.ts`
 * (Carte, Liste, Hiérarchie, Maintenant, Agenda, chantier angles-du-projet, tickets 02 et 03). « Hiérarchie »
 * (ticket 35) est une autre lecture de la carte, organisée pour que l'étape 1 soit en haut ;
 * elle ne remplace pas la liste, qui reste la todo où l'on valide.
 */
export type { Vue };

/**
 * Le repère du tuto de chaque onglet, qui le garde s'il change de place ; et sa petite icône
 * (ticket 04, maquette du carnet `projet-ouvert.html`, `.angles`), lue dans `partage/icones.tsx` :
 * Agenda et Maintenant ont celles de la barre de gauche.
 */
const REPERES: Record<Vue, { tuto: string; Icone: (p: { className: string }) => ReactElement }> = {
  carte: { tuto: 'bascule-carte', Icone: IconeCarte },
  liste: { tuto: 'bascule-liste', Icone: IconeListe },
  hierarchie: { tuto: 'bascule-hierarchie', Icone: IconeCasesReliees },
  agenda: { tuto: 'bascule-agenda', Icone: IconeCalendrier },
  maintenant: { tuto: 'bascule-maintenant', Icone: IconeHorloge },
};

/**
 * La bascule entre les vues, et **sa touche**.
 *
 * *« Sur ordinateur elle mérite une vraie touche du clavier : on bascule sans arrêt pendant
 * qu'on réfléchit »* (SPEC §5.3). C'est la raison d'être de ce fichier : sans touche, la
 * bascule reste un bouton qu'on vise à la souris, et on cesse de basculer — donc on cesse de
 * voir la forme, qui est tout ce que la carte apporte.
 *
 * La touche est **v**, comme la vue : `h` appartient déjà à « hiérarchiser », et la même
 * prudence s'applique — jamais pendant qu'on écrit (la barre d'ajout ne se ferme jamais,
 * donc le curseur y est presque toujours), jamais avec une touche de commande, et jamais
 * quand un panneau est ouvert : dans une fiche, `v` s'écrit dans les notes.
 */
export function useVueDeFeuille(suspendue: boolean) {
  const [vue, setVue] = useState<Vue>('carte');

  useEffect(() => {
    if (suspendue) return;

    const auClavier = (evenement: KeyboardEvent) => {
      const cible = evenement.target as HTMLElement | null;
      const enTrainDEcrire =
        cible?.tagName === 'INPUT' ||
        cible?.tagName === 'TEXTAREA' ||
        cible?.isContentEditable === true;

      if (evenement.key !== 'v' || enTrainDEcrire) return;
      if (evenement.metaKey || evenement.ctrlKey || evenement.altKey) return;

      evenement.preventDefault();
      setVue(angleSuivant);
    };

    window.addEventListener('keydown', auClavier);
    return () => window.removeEventListener('keydown', auClavier);
  }, [suspendue]);

  return { vue, setVue };
}

/**
 * Les onglets. Ils sont **toujours là**, y compris sur une feuille vide : aucune des vues
 * n'est la vraie, et rien ne doit être visible dans l'une et absent de l'autre — sinon la
 * bascule devient un piège. Le bouton ⚙ se pose juste après (`ParametresDeFeuille.tsx`), le
 * sélecteur de profondeur dessous (ticket 13 de retours-testeurs).
 *
 * À l'ordinateur, un rail creusé (ticket 04, `TeteDeFeuille.css`) : chaque angle sa petite icône
 * et son nom, l'angle choisi sur le papier, en relief.
 */
export function BasculeVue({ vue, onBasculer }: { vue: Vue; onBasculer: (vue: Vue) => void }) {
  return (
    <div className="feuille-vue">
      <div className="feuille-bascule" role="tablist" aria-label="comment regarder la feuille">
        {ANGLES.map(({ vue: laVue, nom }) => {
          const { tuto, Icone } = REPERES[laVue];
          return (
            <button
              key={laVue}
              type="button"
              role="tab"
              data-tuto={tuto}
              aria-selected={vue === laVue}
              aria-keyshortcuts="v"
              className={vue === laVue ? 'est-choisi' : ''}
              onClick={() => onBasculer(laVue)}
            >
              <Icone className="feuille-bascule-icone" />
              {nom}
            </button>
          );
        })}
      </div>

      {/* Une touche nue, comme celle de « hiérarchiser » : un rappel, pas un bouton. */}
      <kbd className="feuille-bascule-touche" title="La touche v passe d’une vue à l’autre">
        v
      </kbd>
    </div>
  );
}
